import React from 'react'
import { Box, Typography, Chip } from '@mui/material'
import { useDrag } from 'react-dnd'
import PersonIcon from '@mui/icons-material/Person'
import EmailIcon from '@mui/icons-material/Email'
import PhoneIcon from '@mui/icons-material/Phone'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import NumbersIcon from '@mui/icons-material/Filter1'

const iconForField = (fieldName) => {
  const safeValue =
    typeof fieldName === 'string' ? fieldName : String(fieldName || '')
  const lower = safeValue.toLowerCase()

  if (lower.includes('nombre') || lower.includes('usuario'))
    return <PersonIcon fontSize="small" />
  if (lower.includes('correo') || lower.includes('email'))
    return <EmailIcon fontSize="small" />
  if (lower.includes('tel')) return <PhoneIcon fontSize="small" />
  if (lower.includes('fecha')) return <CalendarTodayIcon fontSize="small" />
  if (lower.includes('hora')) return <AccessTimeIcon fontSize="small" />
  if (
    lower.includes('edad') ||
    lower.includes('monto') ||
    lower.includes('número') ||
    lower.includes('numero')
  ) {
    return <NumbersIcon fontSize="small" />
  }
  return <TextFieldsIcon fontSize="small" />
}

const DraggableFieldBox = ({ field }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'FIELD',
    item: { field },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }))

  return (
    <Box
      ref={drag}
      className="field-card-root"
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        p: '10px',
        mb: '8px',
        minHeight: 76,
        cursor: 'grab',
        opacity: isDragging ? 0.4 : 1,
        transition: 'all 0.15s ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 1,
        },
      }}
    >
      <Box
        className="field-card-content"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          width: '100%',
          minWidth: 0,
        }}
      >
        <Box
          className="field-icon"
          sx={{
            flex: '0 0 auto',
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
            borderRadius: 1,
          }}
        >
          {iconForField(field.label || field.name)}
        </Box>

        <Box
          className="field-text-container"
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '2px',
            overflow: 'hidden',
          }}
        >
          <Typography
            className="field-label"
            sx={{
              fontSize: 13,
              fontWeight: 500,
              lineHeight: 1.2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {field.label || field.name || 'Campo sin etiqueta'}
          </Typography>
          <Typography
            className="field-name"
            sx={{
              fontSize: 11,
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {field.name || 'sin_nombre'}
          </Typography>
        </Box>

        <Chip
          label={field.type || 'Texto'}
          size="small"
          variant="outlined"
          className="field-chip"
          sx={{
            flex: '0 0 auto',
            alignSelf: 'center',
            maxWidth: 80,
            overflow: 'hidden',
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
        />
      </Box>
    </Box>
  )
}

const FieldsSidebar = ({ fields }) => {
  return (
    <Box
      sx={{
        minWidth: 0,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        pb: 1,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ gap: '10px', minWidth: 0 }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
          Biblioteca de campos
        </Typography>

        <Chip
          label={`${fields.length} disponibles`}
          size="small"
          sx={{ flex: '0 0 auto' }}
        />
      </Box>
      <Box sx={{ mt: 2, minWidth: 0 }}>
        {fields.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Carga un archivo para ver los campos disponibles
          </Typography>
        ) : (
          fields.map((field) => {
            const stableKey =
              field.id ||
              `${field.name || 'field'}-${field.type || 'Texto'}-${field.label || 'sin_label'}`

            return <DraggableFieldBox key={stableKey} field={field} />
          })
        )}
      </Box>
    </Box>
  )
}

export default FieldsSidebar
